/**
 * V10 Phase C — Graph 탭 (Timeline Playback + Edge Inspector 통합).
 *
 * 기존 `components/graph/GraphTab.tsx` (overview/ego) 를 재사용하되, 상단 TimelineBar 와
 * 사이드 Edge Inspector 패널을 덮어 씌우는 래퍼. 별도 파일로 분리 — 기존 GraphTab 회귀 방지.
 *
 * asOf 변경 → `useGraphAtAsOf` 로 재조회 → `GraphEdgeView` 에 strength 포함.
 * edge 클릭 → URL `?edgeKey=...` 반영 → Edge Inspector 열림.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type {
	AdminClient,
	EpisodesDensityResponse,
	EpisodesTimeRange,
	GraphEdgeWithStrength,
	OntologyBlock
} from "../../../api/admin-client.ts"
import { GraphTab } from "../../../components/graph/GraphTab.tsx"
import { KIND_COLORS } from "../../../components/graph/graph-colors.ts"
import { KnowledgeGraph, type KnowledgeGraphHandle } from "../../../components/graph/KnowledgeGraph.tsx"
import type { GraphEdge, GraphNode } from "../../../components/graph/types.ts"
import { useAsOf } from "../../../hooks/useAsOf.tsx"
import { useGraphAtAsOf } from "../../../hooks/useGraphAtAsOf.ts"
import { buildKindToBlockMap } from "../../blocks-graph-data.ts"
import { EdgeInspector } from "./EdgeInspector.tsx"
import { TimelineBar } from "./TimelineBar.tsx"

type Props = {
	readonly client: AdminClient
	readonly slug: string
}

/** URL 쿼리에서 edgeKey 파싱. */
function parseEdgeKeyFromUrl(): string | undefined {
	if (typeof window === "undefined") return undefined
	try {
		const url = new URL(window.location.href)
		return url.searchParams.get("edgeKey") ?? undefined
	} catch {
		return undefined
	}
}

function updateEdgeKeyInUrl(next: string | undefined): void {
	if (typeof window === "undefined") return
	try {
		const url = new URL(window.location.href)
		if (next) url.searchParams.set("edgeKey", next)
		else url.searchParams.delete("edgeKey")
		window.history.replaceState(null, "", url.toString())
	} catch {
		// no-op
	}
}

export function GraphTabWithPlayback(props: Props) {
	const { asOf, isHydrated } = useAsOf()
	const [range, setRange] = useState<EpisodesTimeRange | undefined>()
	const [density, setDensity] = useState<EpisodesDensityResponse | undefined>()
	const [blocks, setBlocks] = useState<ReadonlyArray<OntologyBlock>>([])
	const [selectedEdgeKey, setSelectedEdgeKey] = useState<string | undefined>(() => parseEdgeKeyFromUrl())
	const graphRef = useRef<KnowledgeGraphHandle>(null)
	/** 엣지 클릭 힌트 — localStorage 로 영구 숨김 가능 */
	const [edgeHintDismissed, setEdgeHintDismissed] = useState<boolean>(() => {
		try {
			return localStorage.getItem("knowledge-edge-hint-dismissed") === "1"
		} catch {
			return false
		}
	})

	// 서버 검색 — Graph 탭 어느 상태에서도 항상 노출. Enter 또는 버튼으로 `knowledge_search`
	// 하이브리드 (admin JWT) 호출. 결과 클릭 시 기본 GraphTab 모드로 전환해 overview/ego 탐색.
	const [searchQuery, setSearchQuery] = useState("")
	const [searchHits, setSearchHits] = useState<
		ReadonlyArray<{ nodeId: string; domain: string; kind: string; name: string }>
	>([])
	const [searchLoading, setSearchLoading] = useState(false)
	const [fallbackMode, setFallbackMode] = useState(false)
	const [fallbackSeed, setFallbackSeed] = useState<
		{ nodeId: string; name: string; kind: string; domain: string } | undefined
	>()
	const runServerSearch = useCallback(async () => {
		const q = searchQuery.trim()
		if (!q) {
			setSearchHits([])
			return
		}
		setSearchLoading(true)
		try {
			const hits = await props.client.searchSeedNodes(props.slug, q, 20)
			setSearchHits(hits)
		} catch {
			// 검색 실패는 조용히 무시 — 사용자가 다시 시도 가능
		} finally {
			setSearchLoading(false)
		}
	}, [props.client, props.slug, searchQuery])

	const {
		data: graphData,
		isLoading,
		error
	} = useGraphAtAsOf({
		client: props.client,
		slug: props.slug,
		asOf,
		enabled: isHydrated
	})

	useEffect(() => {
		let cancelled = false
		void (async () => {
			try {
				const [r, d, b] = await Promise.all([
					props.client.getEpisodesTimeRange(props.slug),
					props.client.getEpisodesDensity(props.slug, 252),
					props.client.getOntologyBlocks()
				])
				if (!cancelled) {
					setRange(r)
					setDensity(d)
					setBlocks(b)
				}
			} catch {
				// timeline metadata / 블럭 카탈로그 실패해도 graph 는 동작
			}
		})()
		return () => {
			cancelled = true
		}
	}, [props.client, props.slug])

	/** kind 이름 → OntologyBlock 매핑. `block.icon` / `block.category` 를 edge 목록의 시각 언어로 사용. */
	const kindToBlock = useMemo(() => buildKindToBlockMap(blocks), [blocks])

	/** blocks → relation displayName 맵. fallback: raw name */
	const relationDisplayNames = useMemo(() => {
		const m = new Map<string, string>()
		for (const b of blocks) {
			for (const r of b.slice.relations) {
				if (r.displayName) m.set(r.name, r.displayName)
			}
		}
		return m
	}, [blocks])

	/** blocks → kind displayName 맵. fallback: raw kind */
	const kindDisplayNames = useMemo(() => {
		const m = new Map<string, string>()
		for (const b of blocks) {
			for (const k of b.slice.kinds) {
				if (k.displayName) m.set(k.name, k.displayName)
			}
		}
		return m
	}, [blocks])

	/** blocks → relation directionality 맵 */
	const relationDirectionality = useMemo(() => {
		const m = new Map<string, "directed" | "undirected">()
		for (const b of blocks) {
			for (const r of b.slice.relations) {
				if (r.directionality) m.set(r.name, r.directionality)
			}
		}
		return m
	}, [blocks])

	const nodes: GraphNode[] = useMemo(
		() => (graphData?.nodes ?? []).map((n) => ({ ...n })),
		[graphData?.nodes]
	)
	const edges: GraphEdge[] = useMemo(
		() =>
			(graphData?.edges ?? []).map((e) => ({
				id: e.id,
				source: e.source,
				target: e.target,
				relation: e.relation,
				...(e.confidence !== undefined ? { confidence: e.confidence } : {})
			})),
		[graphData?.edges]
	)

	/** id → 노드 lookup — edge 목록에서 이름/종류 병기에 사용. */
	const nodeById = useMemo(() => {
		const m = new Map<string, GraphNode>()
		for (const n of nodes) m.set(n.id, n)
		return m
	}, [nodes])

	const strengthByEdgeKey = useMemo(() => {
		const map = new Map<string, GraphEdgeWithStrength>()
		for (const e of graphData?.edges ?? []) {
			const k = `${e.fromId}|${e.relation}|${e.toId}`
			map.set(k, e)
		}
		return map
	}, [graphData?.edges])

	const selected = useMemo(() => {
		if (!selectedEdgeKey) return null
		const hit = strengthByEdgeKey.get(selectedEdgeKey)
		if (!hit) {
			// fallback: URL 파라미터가 있지만 매칭되는 edge 가 없으면 파싱만이라도 시도
			const parts = selectedEdgeKey.split("|")
			if (parts.length === 3) {
				return {
					fromId: parts[0] ?? "",
					toId: parts[2] ?? "",
					relation: parts[1] ?? "",
					strength: 0
				}
			}
			return null
		}
		return {
			fromId: hit.fromId,
			toId: hit.toId,
			relation: hit.relation,
			...(hit.strength !== undefined ? { strength: hit.strength } : {}),
			...(hit.invalidAt ? { invalidAt: hit.invalidAt } : {})
		}
	}, [selectedEdgeKey, strengthByEdgeKey])

	const handleEdgeSelect = useCallback((edgeKey: string | undefined) => {
		setSelectedEdgeKey(edgeKey)
		updateEdgeKeyInUrl(edgeKey)
	}, [])

	// 검색 바 — 정상/에러/폴백 어떤 상태에서도 항상 렌더.
	const searchBar = (
		<div
			style={{
				display: "flex",
				gap: 8,
				alignItems: "center",
				padding: "10px 14px",
				borderBottom: "1px solid var(--pk-border)",
				background: "var(--pk-bg)",
				flexWrap: "wrap"
			}}
		>
			<input
				type="text"
				placeholder="노드 검색 — 이름/키워드 입력 후 Enter (knowledge_search 하이브리드)"
				value={searchQuery}
				onChange={(e) => setSearchQuery(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						e.preventDefault()
						void runServerSearch()
					}
				}}
				style={{
					flex: "1 1 320px",
					padding: "6px 10px",
					border: "1px solid var(--pk-border)",
					borderRadius: 6,
					background: "var(--pk-bg-elevated)",
					color: "inherit"
				}}
			/>
			<button
				type="button"
				className="btn btn-ghost btn-sm"
				onClick={() => void runServerSearch()}
				disabled={searchLoading}
			>
				{searchLoading ? "검색 중…" : "서버 검색"}
			</button>
			{(error || fallbackMode) && (
				<button
					type="button"
					className="btn btn-ghost btn-sm"
					onClick={() => {
						setFallbackMode(false)
						setSearchHits([])
					}}
				>
					Timeline 복귀
				</button>
			)}
		</div>
	)

	const searchResultsList = searchHits.length > 0 && (
		<div
			style={{
				borderBottom: "1px solid var(--pk-border)",
				maxHeight: 240,
				overflowY: "auto",
				background: "var(--pk-bg)"
			}}
		>
			<div
				style={{
					padding: "6px 14px",
					fontSize: 11,
					color: "var(--pk-text-muted)",
					display: "flex",
					alignItems: "center",
					gap: 8
				}}
			>
				<span>검색 결과 {searchHits.length} — 클릭 시 기본 Graph 뷰(ego 탐색)로 전환</span>
				<button
					type="button"
					className="btn btn-ghost btn-sm"
					style={{ marginLeft: "auto" }}
					onClick={() => setSearchHits([])}
				>
					닫기
				</button>
			</div>
			{searchHits.map((hit) => (
				<button
					key={hit.nodeId}
					type="button"
					onClick={() => {
						setFallbackSeed(hit)
						setFallbackMode(true)
						setSearchHits([])
					}}
					style={{
						display: "block",
						width: "100%",
						textAlign: "left",
						padding: "8px 14px",
						border: 0,
						borderTop: "1px solid var(--pk-border)",
						background: "transparent",
						color: "inherit",
						cursor: "pointer",
						fontSize: 13
					}}
				>
					<div style={{ fontWeight: 500 }}>{hit.name}</div>
					<div style={{ fontSize: 11, color: "var(--pk-text-muted)", marginTop: 2 }}>
						{hit.domain} · {hit.kind} · {hit.nodeId}
					</div>
				</button>
			))}
		</div>
	)

	// asOf 그래프 로드 실패 or 사용자가 검색 결과 클릭 시 → 기본 GraphTab 으로 폴백
	// (overview + ego + 내부 검색 재사용). GraphTab 이 자체 검색창 을 갖고 있으므로
	// 폴백 모드에선 GraphTabWithPlayback 의 searchBar 를 생략해 검색창 중복을 피한다.
	if (error || fallbackMode) {
		return (
			<div className="panel" style={{ padding: 0, overflow: "hidden" }}>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: 8,
						padding: "10px 14px",
						borderBottom: "1px solid var(--pk-border)",
						background: "var(--pk-bg)",
						flexWrap: "wrap"
					}}
				>
					<span style={{ fontSize: 12, color: "var(--pk-text-muted)", flex: 1 }}>
						{error
							? `Timeline/Inspector 로드 실패 (${error.slice(0, 80)}…) — 아래 기본 Graph 뷰로 폴백됨`
							: "검색 결과로 ego 탐색 중 — Timeline 은 숨김"}
					</span>
					<button
						type="button"
						className="btn btn-ghost btn-sm"
						onClick={() => {
							setFallbackMode(false)
							setSearchHits([])
						}}
					>
						Timeline 복귀
					</button>
				</div>
				<GraphTab
					slug={props.slug}
					client={props.client}
					{...(fallbackSeed
						? {
								initialSeedId: fallbackSeed.nodeId,
								initialSeedName: fallbackSeed.name,
								initialSeedKind: fallbackSeed.kind,
								initialSeedDomain: fallbackSeed.domain
							}
						: {})}
				/>
			</div>
		)
	}

	return (
		<div className="panel" style={{ padding: 0, overflow: "hidden" }}>
			<TimelineBar
				range={range?.min && range?.max ? { min: range.min, max: range.max } : undefined}
				buckets={density?.buckets ?? []}
			/>
			{searchBar}
			{searchResultsList}

			<div style={{ display: "flex", minHeight: "calc(100vh - 380px)", position: "relative" }}>
				<div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
					{isLoading && nodes.length === 0 ? (
						<div style={{ padding: 40, textAlign: "center", color: "var(--pk-text-muted)" }}>
							불러오는 중…
						</div>
					) : (
						<div style={{ position: "relative", flex: 1, minHeight: 0 }}>
							<button
								type="button"
								className="btn btn-ghost btn-sm"
								onClick={() => graphRef.current?.fitView()}
								title="그래프를 캔버스에 맞춰 재정렬"
								style={{
									position: "absolute",
									top: 12,
									right: 12,
									zIndex: 5,
									background: "var(--pk-bg-elevated)",
									border: "1px solid var(--pk-border)"
								}}
							>
								⟲ Fit View
							</button>
							<KnowledgeGraph
								ref={graphRef}
								nodes={nodes}
								edges={edges}
								onLinkSelect={(fromId: string, toId: string, relation: string) => {
									handleEdgeSelect(`${fromId}|${relation}|${toId}`)
								}}
								{...(selectedEdgeKey ? { highlightedEdgeKey: selectedEdgeKey } : {})}
								relationDisplayNames={relationDisplayNames}
								kindDisplayNames={kindDisplayNames}
								relationDirectionality={relationDirectionality}
							/>
							{/* 범례 오버레이 — 현재 표시 중인 unique kind 목록. Fit View 버튼 바로 아래 */}
							<PlaybackGraphLegend nodes={nodes} kindDisplayNames={kindDisplayNames} />
							{/* 엣지 클릭 힌트 — Inspector 가 열리지 않았을 때만 표시 */}
							{!edgeHintDismissed && !selectedEdgeKey && (
								<div
									style={{
										position: "absolute",
										bottom: 12,
										left: 12,
										zIndex: 4,
										background: "var(--pk-bg-elevated)",
										border: "1px solid var(--pk-border)",
										borderRadius: "var(--pk-radius-md)",
										padding: "6px 10px",
										fontSize: 12,
										color: "var(--pk-text-muted)",
										display: "flex",
										alignItems: "center",
										gap: 6,
										pointerEvents: "auto"
									}}
								>
									<span>💡 엣지를 클릭하면 상세 정보가 열립니다</span>
									<button
										type="button"
										onClick={() => {
											setEdgeHintDismissed(true)
											try {
												localStorage.setItem("knowledge-edge-hint-dismissed", "1")
											} catch {
												// no-op
											}
										}}
										aria-label="힌트 닫기"
										style={{
											background: "transparent",
											border: "none",
											cursor: "pointer",
											color: "var(--pk-text-muted)",
											fontSize: 12,
											padding: "0 2px",
											lineHeight: 1
										}}
									>
										✕
									</button>
								</div>
							)}
						</div>
					)}
				</div>

				<div
					style={{
						width: 380,
						borderLeft: "1px solid var(--pk-border)",
						background: "var(--pk-bg-elevated)",
						overflowY: "auto"
					}}
				>
					<EdgeInspector
						client={props.client}
						slug={props.slug}
						selected={selected}
						fromNode={selected ? nodeById.get(selected.fromId) : undefined}
						toNode={selected ? nodeById.get(selected.toId) : undefined}
						kindToBlock={kindToBlock}
						onClose={() => handleEdgeSelect(undefined)}
						relationDisplayNames={relationDisplayNames}
					/>
				</div>
			</div>
		</div>
	)
}

/**
 * Timeline Playback 캔버스용 범례 오버레이.
 * Fit View 버튼이 우상단 zIndex 5 를 차지하므로 이 범례는 그 아래(60px 오프셋)에 배치.
 */
function PlaybackGraphLegend({
	nodes,
	kindDisplayNames
}: {
	readonly nodes: ReadonlyArray<GraphNode>
	readonly kindDisplayNames: ReadonlyMap<string, string>
}) {
	const [expanded, setExpanded] = useState(false)

	const uniqueKinds = useMemo(() => {
		const seen = new Set<string>()
		for (const n of nodes) seen.add(n.kind)
		return [...seen].sort()
	}, [nodes])

	if (uniqueKinds.length === 0) return null

	const MAX_VISIBLE = 4
	const visible = expanded ? uniqueKinds : uniqueKinds.slice(0, MAX_VISIBLE)
	const overflow = uniqueKinds.length - MAX_VISIBLE

	return (
		<div
			style={{
				position: "absolute",
				top: 60,
				right: 12,
				zIndex: 4,
				background: "var(--pk-bg-elevated)",
				border: "1px solid var(--pk-border)",
				borderRadius: "var(--pk-radius-md)",
				padding: "8px 10px",
				fontSize: 11,
				minWidth: 120,
				maxWidth: 200,
				pointerEvents: "auto"
			}}
		>
			{visible.map((kind) => {
				const color = KIND_COLORS[kind] ?? KIND_COLORS.default ?? "#6e7681"
				const label = kindDisplayNames.get(kind) ?? kind
				return (
					<div
						key={kind}
						style={{
							display: "flex",
							alignItems: "center",
							gap: 6,
							marginBottom: 4,
							overflow: "hidden"
						}}
					>
						<span
							style={{
								width: 8,
								height: 8,
								borderRadius: "50%",
								background: color,
								flexShrink: 0
							}}
							aria-hidden
						/>
						<span
							style={{
								color: "var(--pk-text-muted)",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis"
							}}
							title={kind}
						>
							{label}
						</span>
					</div>
				)
			})}
			{!expanded && overflow > 0 && (
				<button
					type="button"
					onClick={() => setExpanded(true)}
					style={{
						background: "transparent",
						border: "none",
						cursor: "pointer",
						fontSize: 11,
						color: "var(--pk-text-muted)",
						padding: 0,
						marginTop: 2
					}}
				>
					+{overflow} 더보기
				</button>
			)}
			{expanded && overflow > 0 && (
				<button
					type="button"
					onClick={() => setExpanded(false)}
					style={{
						background: "transparent",
						border: "none",
						cursor: "pointer",
						fontSize: 11,
						color: "var(--pk-text-muted)",
						padding: 0,
						marginTop: 2
					}}
				>
					접기
				</button>
			)}
		</div>
	)
}
